/**
 * A "state pack" is the full configuration for a tailored ScamDefense
 * experience. The generic site uses the default pack; /states/$slug routes
 * mount a specific pack via StateProvider.
 */
export type StateAgency = {
  name: string
  description: string
  url?: string
  phone?: string
}

export type StateTopScam = {
  /** Internal slug, e.g. "investment-fraud". Used to lookup example overrides. */
  id: string
  /** Display name, e.g. "Investment Fraud" */
  name: string
  /** One-line description for homepage cards. */
  blurb: string
  /** Most recent reported losses for this scam in this state, if known. */
  lossUsd?: number
}

/**
 * Optional tenant overlay applied on top of a state pack when a paying
 * sponsor (state AG, credit union, agency) deploys a customized variant.
 *
 * The overlay swaps brand voice and reporting routes without changing the
 * underlying course or data layer.
 */
export type StateTenant = {
  /** Stable identifier, e.g. "md-treasury". Used in URLs (`?tenant=md-treasury`). */
  id: string
  /** Brand display name, e.g. "Maryland Office of the State Treasurer". */
  brandName: string
  /** Optional one-line tagline shown under the brand name. */
  tagline?: string
  /** Hex color override for primary accent. Defaults to ScamDefense violet. */
  primaryColor?: string
  /** Public-facing logo (small mark) URL or null. */
  logoUrl?: string
  /** Hotline shown above generic agencies. */
  hotline?: {
    name: string
    phone: string
    description: string
    url?: string
  }
  /** How attribution is rendered on tenant pages. */
  attributionMode?: 'powered-by' | 'co-branded' | 'white-label'
  /** Optional sponsor strip shown in the header. */
  sponsorStrip?: {
    label: string
    href?: string
  }
}

export type StatePack = {
  /** ISO state code, lowercase, e.g. "ny". null = generic / no state. */
  slug: string | null
  /** Display name, e.g. "New York". null for generic. */
  name: string | null
  /**
   * Optional state-specific hero artwork (e.g. shield + state outline).
   * Path is served from `public/`, so use a leading slash: "/states/md.jpg".
   * When set, the state page hero matches the national homepage hero
   * style (dark navy + image background) instead of the lighter text block.
   *
   * Takes precedence over `heroMap` if both are set.
   */
  heroImage?: string
  /**
   * Optional procedurally-rendered map shown in the dark hero. When set
   * (and no `heroImage`), the state page hero renders the SVG map as the
   * key visual: either the state's outline alone, or a US map with this
   * state highlighted.
   */
  heroMap?: 'outline' | 'us-map'
  /**
   * Optional sponsor banner copy, shown in the "Presented by" pill.
   * Only used when slug !== null.
   */
  sponsor?: {
    label: string
    url?: string
  }
  /** Top reported scams for this state, sorted by loss volume (desc). */
  topScams: StateTopScam[]
  /** State-specific agencies users can call if they've been scammed. */
  agencies: StateAgency[]
  /**
   * Per-slide example overrides. Keyed by slide id; arbitrary shape.
   * Slide components opt-in to override by reading from useStateExample(slideId).
   */
  exampleOverrides?: Record<string, unknown>
  /**
   * Tenant overlay applied to this pack. Set at runtime (StateProvider) when
   * a `?tenant=` query param resolves a registered tenant. Authors should
   * never hardcode this on the source pack.
   */
  tenant?: StateTenant | null
}

export const GENERIC_PACK: StatePack = {
  slug: null,
  name: null,
  topScams: [],
  agencies: [
    {
      name: 'IC3 (FBI Internet Crime Complaint Center)',
      description: 'Report internet-enabled crime and fraud.',
      url: 'https://www.ic3.gov',
    },
    {
      name: 'FTC (Federal Trade Commission)',
      description: 'Report scams and identity theft.',
      url: 'https://reportfraud.ftc.gov',
      phone: '1-877-382-4357',
    },
    {
      name: 'AARP Fraud Watch Network Helpline',
      description: 'Talk to a trained fraud specialist.',
      phone: '1-877-908-3360',
    },
  ],
}
